/**
 * GelEnvelopeDashboardPageV2 — proper v2 redesign (G126).
 *
 * Dashboard pattern: KPI tiles + envelope inventory + hash-chain
 * verification widget.
 *
 * Reuses every backend code path from v1 GelEnvelopeDashboardPage:
 *   - api.gelGetMetrics
 *   - api.gelListEnvelopes / gelGetEnvelope
 *   - api.gelVerifyHashChain
 *
 * v1 stays as fallback via /gel-dashboard?legacy=1.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import * as api from '../services/api';
import {
  RefreshCw, ShieldCheck, Activity, CheckCircle, AlertTriangle, Eye,
  ArrowRight, Lock,
} from 'lucide-react';
import {
  Button, Pill, Card, MetricCard, Slideout, Table,
} from '../components/v2';
/* G395 — Pagination for the envelopes table. Envelopes are append-only
 * audit rows; without paging the old `.slice(0,20)` silently truncated
 * everything older than the latest 20. */
import { Pagination, usePaginationState } from '../components/common/Pagination';

import GelEnvelopeDashboardPage from './GelEnvelopeDashboardPage';

function GelEnvelopeDashboardPageV2Inner({ agentScope }) {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { session } = useAuth();
  const tenantId = session?.tenantId;

  const [metrics, setMetrics] = useState(null);
  const [envelopes, setEnvelopes] = useState([]);
  const [chainStatus, setChainStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      /* G133 — when scoped (native/external), filter envelopes accordingly. */
      const scopeArg = agentScope ? { agentScope } : {};
      const [m, e] = await Promise.all([
        api.gelGetMetrics?.(tenantId, scopeArg)?.catch(() => null),
        api.gelListEnvelopes?.({ tenantId, limit: 50, ...scopeArg })?.catch(() => null),
      ]);
      setMetrics(m?.data || m || null);
      const list = Array.isArray(e?.data?.items) ? e.data.items
                : Array.isArray(e?.items) ? e.items
                : Array.isArray(e?.data) ? e.data
                : Array.isArray(e) ? e : [];
      setEnvelopes(list);
    } finally {
      setLoading(false);
    }
  }, [tenantId, agentScope]);
  useEffect(() => { reload(); }, [reload]);

  const handleVerify = async () => {
    try {
      const r = await api.gelVerifyHashChain?.(tenantId);
      const data = r?.data || r;
      setChainStatus(data);
      addToast({
        tone: data?.intact ? 'success' : 'danger',
        title: data?.intact ? 'Hash chain intact' : 'Hash chain BROKEN',
        description: data?.intact
          ? `${data.verifiedCount || data.events || 0} events verified, no breaks.`
          : `${data?.breakCount || 0} break(s) detected at ${data?.firstBreakAt || 'unknown position'}`,
      });
    } catch (err) {
      addToast({ tone: 'danger', title: 'Verification failed', description: err?.message });
    }
  };

  const [selected, setSelected] = useState(null);
  const openSlideout = (row) => setSelected(row);
  const closeSlideout = () => setSelected(null);

  const kpis = useMemo(() => ({
    signed24h:    metrics?.signed24h || metrics?.envelopesSigned24h || envelopes.length,
    verified24h:  metrics?.verified24h || envelopes.filter(e => e.verified).length,
    chainEvents:  metrics?.chainLength || metrics?.totalEvents || envelopes.length,
    integrity:    chainStatus?.intact === false ? 'BROKEN' : (chainStatus?.intact ? 'OK' : 'UNVERIFIED'),
    p95SigLatency: metrics?.p95SigLatencyMs ?? null,
    avgPayloadKB: metrics?.avgPayloadKB ?? null,
  }), [metrics, envelopes, chainStatus]);

  /* G395 — Pagination for the GEL envelopes table. Pre-G395 the render used
   * `envelopes.slice(0, 20)` which silently dropped older rows in an
   * audit context where completeness matters. */
  const pgEnvelopes = usePaginationState(20);
  useEffect(() => { pgEnvelopes.setPage(0); }, [envelopes.length]); // eslint-disable-line

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1500px] mx-auto">
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">
            {agentScope === 'native' ? 'Native · Execution Audit'
             : agentScope === 'external' ? 'External · Execution Audit'
             : 'GEL · Envelope Dashboard'}
          </h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            Cryptographically signed execution envelopes, hash-chain integrity, and audit trail snapshots
            {agentScope === 'native' && ' — scoped to native Agentlets (Lets, AutoLets, SysLets).'}
            {agentScope === 'external' && ' — scoped to external (ExteLet) agents.'}
            {!agentScope && '.'}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
          <Button variant="primary" size="sm" iconLeft={<ShieldCheck size={13} />} onClick={handleVerify}>
            Verify hash chain
          </Button>
        </div>
      </header>

      {/* Hash chain status banner */}
      {chainStatus && (
        <div className={'rounded-v2-md border-l-4 px-4 py-3 mb-5 flex items-center gap-3 ' +
          (chainStatus.intact
            ? 'border-v2-success bg-v2-success-soft'
            : 'border-v2-danger bg-v2-danger-soft')}>
          <div className={'w-9 h-9 rounded-full flex items-center justify-center ' + (chainStatus.intact ? 'bg-v2-success-soft' : 'bg-v2-danger-soft')}>
            {chainStatus.intact ? <CheckCircle size={20} className="text-v2-success" /> : <AlertTriangle size={20} className="text-v2-danger" />}
          </div>
          <div className="flex-1">
            <div className="text-[14px] font-bold text-v2-ink">
              Hash chain: {chainStatus.intact ? 'Intact ✓' : 'BREAK DETECTED'}
            </div>
            <div className="text-[12px] text-v2-ink-secondary">
              {chainStatus.intact
                ? `${chainStatus.verifiedCount || chainStatus.events || 0} events verified · last check ${new Date().toLocaleTimeString()}`
                : `Break at position ${chainStatus.firstBreakAt || '?'} — initiate forensic review immediately.`}
            </div>
          </div>
          {/* G436 — Open forensic review deep-link. Hidden when the
              hash chain is intact; only fires on a real break. */}
          {!chainStatus.intact && (
            <Button variant="danger" size="sm" onClick={() => navigate('/workflow-audit?incident=hashchain')}>Open forensic review</Button>
          )}
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <MetricCard label="Signed / 24h"      value={(kpis.signed24h ?? 0).toLocaleString()} tone="cyan" />
        <MetricCard label="Verified / 24h"    value={(kpis.verified24h ?? 0).toLocaleString()} tone="success" />
        <MetricCard label="Chain events"      value={(kpis.chainEvents ?? 0).toLocaleString()} tone="trust" />
        <MetricCard label="Chain integrity"   value={kpis.integrity}
          tone={kpis.integrity === 'OK' ? 'success' : kpis.integrity === 'BROKEN' ? 'danger' : 'muted'} />
        <MetricCard label="P95 sig latency"   value={kpis.p95SigLatency == null ? '—' : `${kpis.p95SigLatency} ms`} tone="action" />
        <MetricCard label="Avg payload"       value={kpis.avgPayloadKB == null ? '—' : `${kpis.avgPayloadKB} KB`} tone="muted" />
      </div>

      {/* Recent envelopes table */}
      <Card>
        {/* G367 — Was a /gel-dashboard?legacy=1 bounce for a "full
            envelope browser". The V2 table below already shows the
            full envelope inventory with row drill-in; the V1 trip was
            redundant. */}
        <Card.Header title={`Recent envelopes · ${envelopes.length}`} />
        <Card.Body>
          {envelopes.length === 0 ? (
            <p className="text-v2-ink-muted text-[12.5px]">No envelopes recorded in this window.</p>
          ) : (
            <Table
              ariaLabel="GEL envelopes"
              density="compact"
              columns={[
                { key: 'id', header: 'Envelope ID', render: (e) => (
                  <code className="font-mono-v2 text-[11px] text-v2-ink-secondary">{(e.id || e.envelopeId || '').slice(0, 12)}…</code>
                )},
                { key: 'agent', header: 'Agent', render: (e) => (
                  <span className="text-[12px]">{e.agentName || e.agentId || '—'}</span>
                )},
                { key: 'execution', header: 'Execution', render: (e) => (
                  <code className="font-mono-v2 text-[11px] text-v2-ink-muted">{(e.executionId || '').slice(0, 10)}…</code>
                )},
                { key: 'verified', header: 'Verified', render: (e) => (
                  <Pill tone={e.verified ? 'success' : 'warning'} size="sm" dot>{e.verified ? 'verified' : 'pending'}</Pill>
                )},
                { key: 'timestamp', header: 'Signed at', align: 'right', render: (e) => (
                  <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{e.signedAt || e.timestamp ? new Date(e.signedAt || e.timestamp).toLocaleString() : '—'}</span>
                )},
                { key: 'actions', header: '', align: 'right', render: (e) => (
                  <Button variant="ghost" size="sm" onClick={(ev) => { ev.stopPropagation(); openSlideout(e); }} title="View envelope">
                    <Eye size={13} />
                  </Button>
                )},
              ]}
              rows={pgEnvelopes.paginate(envelopes)}
              rowKey={(e) => e.id || e.envelopeId}
              onRowClick={openSlideout}
            />
          )}
          {envelopes.length > pgEnvelopes.pageSize && (
            <div className="mt-3">
              <Pagination
                totalItems={envelopes.length}
                page={pgEnvelopes.page}
                pageSize={pgEnvelopes.pageSize}
                onPageChange={pgEnvelopes.setPage}
                onPageSizeChange={pgEnvelopes.setPageSize}
                noun="envelopes"
              />
            </div>
          )}
        </Card.Body>
      </Card>

      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `Envelope ${selected.id || selected.envelopeId} detail` : 'Envelope detail'}
        width={520}
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'payload',  label: 'Payload' },
          { id: 'signature', label: 'Signature' },
          { id: 'chain',    label: 'Hash chain' },
        ]}
        activeTabId="overview"
        onTabChange={() => {}}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Pill tone={selected.verified ? 'success' : 'warning'} size="sm" dot>
                {selected.verified ? 'verified' : 'pending'}
              </Pill>
              <Pill tone="trust" size="sm">
                <Lock size={10} className="inline mr-1" />
                signed
              </Pill>
            </div>
            <h2 className="text-[16px] font-bold tracking-tight m-0 font-mono-v2">{(selected.id || selected.envelopeId || '').slice(0, 24)}…</h2>
            <p className="text-[12px] text-v2-ink-secondary">{selected.agentName || selected.agentId || '—'}</p>
          </div>
        )}
      >
        {selected && (
          <Card padding="sm">
            {/* G367 — Was a /gel-dashboard?legacy=1 "Full view" bounce.
                The full envelope JSON is rendered directly below — no
                additional V1 trip needed. */}
            <Card.Header title="Envelope detail" />
            <Card.Body>
              <pre className="text-[10.5px] font-mono-v2 text-v2-ink-secondary bg-v2-muted rounded-v2-sm p-3 max-h-[400px] overflow-auto">
                {JSON.stringify(selected, null, 2)}
              </pre>
            </Card.Body>
          </Card>
        )}
      </Slideout>
    </div>
  );
}

export default function ResolvedGelEnvelopeDashboardPage(props) {
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy) return <GelEnvelopeDashboardPage {...props} />;
  return <GelEnvelopeDashboardPageV2Inner {...props} />;
}

export { GelEnvelopeDashboardPageV2Inner };
